import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "shared/UI/Modal/ConfirmationModal/ConfirmationModal";
import { ActivityEntry, GeneralActivitySelector } from "./GeneralActivitySelector";
import { useTrackerContext } from "core/state/TrackerContext/TrackerContext";
import { TrackerType } from "core/state/Trackers/TrackerType";
import Button from "shared/UI/Buttons/Button";
import { trackSaveHealthData } from "core/monitoring/Events";
import { useAppReviewContext } from "core/state/AppReviewContext/AppReviewContext";

export const GeneralActivityInputModal = ({ onClose }: { onClose: VoidFunction }) => {
  const [selectedActivities, setSelectedActivities] = useState<ActivityEntry[]>([
    {
      name: "",
      duration: 0,
    },
  ]);

  const { handlePositiveMoment } = useAppReviewContext();
  const { setTrackerData, getCurrentTrackerValue } = useTrackerContext();
  const { t } = useTranslation();

  const previouslyLoggedDuration = getCurrentTrackerValue(TrackerType.Activity);
  const selectedActivityDuration = selectedActivities.reduce((acc, cur) => acc + (cur.duration ?? 0), 0);

  function onCancel() {
    onClose();
  }

  function onSave() {
    trackSaveHealthData(TrackerType[TrackerType.Activity]);
    setTrackerData(TrackerType.Activity, selectedActivityDuration + previouslyLoggedDuration);
    onClose();
    handlePositiveMoment();
  }

  return (
    <ConfirmationModal
      title={t("wellbeing.trackers.activity.addData.heading")}
      cancelButtonText={t("wellbeing.trackers.activity.addData.cancel")}
      confirmButtonText={t("wellbeing.trackers.activity.addData.save")}
      confirmButtonDisabled={selectedActivityDuration <= 0}
      onCancel={onCancel}
      onConfirm={onSave}
    >
      <div className="flex flex-col">
        <p className="mb-2">{t("wellbeing.trackers.activity.addData.info")}</p>
        {selectedActivities.map((activity, index) => {
          return (
            <GeneralActivitySelector
              key={`${activity.name}-entry-${index}`}
              activityEntry={activity}
              onActivityChanged={(activity) => {
                const activitiesToUpdate = [...selectedActivities];
                activitiesToUpdate.splice(index, 1, activity);
                setSelectedActivities(activitiesToUpdate);
              }}
            />
          );
        })}
        <div className="justify-items-end">
          <span className="float-right mt-1 min-w-[50px]">
            <Button
              variant="secondary"
              text={"+"}
              aria-label={t("wellbeing.trackers.activity.addData.addActivityLabel")}
              onClick={() =>
                setSelectedActivities([
                  ...selectedActivities,
                  {
                    name: "",
                    duration: 0,
                  },
                ])
              }
            />
          </span>
        </div>
        <p className="mt-4 whitespace-pre-line" role="note">
          {t("wellbeing.trackers.activity.addData.activitiesLoggedMessage", {
            current: selectedActivityDuration,
            previous: previouslyLoggedDuration,
            total: selectedActivityDuration + previouslyLoggedDuration,
          })}
        </p>
      </div>
    </ConfirmationModal>
  );
};
